import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Helper function to extract error message
const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const firstError = detail[0];
    return firstError?.msg || firstError?.message || defaultMessage;
  } else if (typeof detail === 'object' && detail !== null) {
    return detail.msg || detail.message || defaultMessage;
  } else if (typeof detail === 'string') {
    return detail;
  }
  return error?.message || defaultMessage;
};

const EmailConfirmationPage = ({ token }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const confirmEmailChange = async () => {
      try {
        const response = await axios.get(`${API}/confirm-email-change?token=${token}`);
        setSuccess(true);
        setNewEmail(response.data.new_email);
      } catch (err) {
        setError(extractErrorMessage(err, 'Erreur lors de la confirmation'));
      } finally {
        setIsLoading(false);
      }
    };

    confirmEmailChange();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#1A1E2E'}}>
        <div style={{color: '#FFFFFF'}} data-testid="email-confirmation-loading">
          Confirmation en cours...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#1A1E2E'}}>
      <div className="w-full max-w-md p-8 rounded-lg shadow-2xl" style={{backgroundColor: '#2C3148'}}>
        {success ? (
          <>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#00FFB3'}} data-testid="email-confirmation-success">
                E-mail confirmé !
              </h2>
              <p className="mb-4" style={{color: '#FFFFFF'}}>
                Votre adresse e-mail a été mise à jour avec succès.
              </p>
              <div className="p-4 rounded mb-6" style={{
                backgroundColor: 'rgba(0, 255, 179, 0.1)',
                border: '1px solid #00FFB3'
              }}>
                <p className="text-sm" style={{color: '#00FFB3'}}>
                  Nouvelle adresse : <strong data-testid="new-email-address">{newEmail}</strong>
                </p>
              </div>
              <p className="text-sm mb-6" style={{color: '#8A92B2'}}>
                Vous pouvez maintenant vous connecter avec votre nouvelle adresse e-mail.
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full py-3 px-4 rounded-md font-medium transition-all duration-300"
              style={{
                backgroundColor: '#4A5BFF',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5D6CFF';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#4A5BFF';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="email-confirmation-login-btn"
            >
              Se connecter
            </button>
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#FF5757'}} data-testid="email-confirmation-error">
                Erreur de confirmation
              </h2>
              <p className="mb-4" style={{color: '#FFFFFF'}}>
                {error}
              </p>
              <p className="text-sm mb-6" style={{color: '#8A92B2'}}>
                Le lien de confirmation est peut-être expiré ou invalide. Veuillez réessayer de changer votre e-mail.
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full py-3 px-4 rounded-md font-medium transition-all duration-300"
              style={{
                backgroundColor: '#1C2133',
                color: '#8A92B2',
                border: '1px solid #4A5BFF'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#242A3D';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1C2133';
                e.currentTarget.style.color = '#8A92B2';
              }}
              data-testid="email-confirmation-back-btn"
            >
              Retour à la connexion
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmationPage;
